import type { ImageCompressionOutcome } from "./operations";

/**
 * Compress Image: what "Don't change dimensions" means and how a result is described.
 *
 * Someone who types a size limit ("200 KB" for a form) expects a file at or under it, so for Custom Target the
 * picture may be made smaller in pixels when quality alone cannot reach the limit, unless they deliberately tick
 * "Don't change dimensions". Medium and Strong stay exactly as they were: they never change pixel size.
 */
export type CompressMode = "medium" | "strong" | "quality" | "target";

/** The tick box starts empty: resizing to reach a typed target is the default. */
export const DEFAULT_KEEP_DIMENSIONS = false;

/** Only Custom Target may shrink pixels, and only while the box is empty. Presets and manual quality never do. */
export function effectiveKeepDimensions(mode: CompressMode, tickedKeep: boolean): boolean {
  return mode === "target" ? tickedKeep : true;
}

export function formatBytes(n: number): string {
  return `${(n / 1024).toFixed(n < 10240 ? 1 : 0)} KB (${n.toLocaleString("en-US")} bytes)`;
}

/** One honest sentence about a finished (not kept-original) compression. */
export function describeCompressResult(r: ImageCompressionOutcome, mode: CompressMode, keepDimensions: boolean): string {
  const fmt = r.extension.toUpperCase() + (r.hadTransparency ? ", transparency kept" : "");
  const dims = `${r.width} x ${r.height} px`;
  if (mode === "quality") return `${formatBytes(r.size)} - ${dims} (same size as the original) - ${fmt}.`;
  if (r.metTarget && r.resized) {
    return `Reached ${formatBytes(r.size)} by resizing to ${dims} (from ${r.originalWidth} x ${r.originalHeight} px) - ${fmt}. Tick "Don't change dimensions" to keep the pixel size instead.`;
  }
  if (r.metTarget) return `${formatBytes(r.size)} - ${dims} (same size as the original) - ${fmt}. Target reached.`;
  const why = mode === "target" && keepDimensions ? " without changing dimensions" : "";
  return `${formatBytes(r.size)} - ${dims} - ${fmt}. Target not reached${why} - this is the smallest possible${mode === "target" && keepDimensions ? "; untick \"Don't change dimensions\" to let the picture be made smaller in pixels" : ""}.`;
}
